import AppCard from '@nxsys/components/AppCard';
import { PayFrequencyValue } from '@nxsys/components/AppPayFrequencySelect/type';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import { OptionType } from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useDebounce } from '@nxsys/hooks/useDebounce';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { revenueActions } from '@nxsys/store/slices/revenueFilings/reducer/revenueSlice';
import { PaginationParamsType } from '@nxsys/types/models/Common';
import { PsrBatchRecordType } from '@nxsys/types/models/revenueFilings/psr';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';
import AppButton from '@nxsys/components/AppButton';
import { downloadPsrBatch, onExportPSRError } from '@nxsys/utils/psr';
import { showToast } from '@nxsys/utils/toast';
import jwtAxios from '@nxsys/services/axios';
import { endPoints } from '@nxsys/services/endpoints';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PSRHeader from '../../Headers/PSRHeader';
import SelectDataTypeModal, {
  DownloadType,
} from '../../modals/SelectDataTypeModal';
import { getPSRBatchColumns } from './columns';

export type PSRBatchFilterType = {
  taxYear: string;
  search: string;
  payFrequency: PayFrequencyValue | null;
  taxPeriod: number | null;
  companyRefs: OptionType[];
};

type Props = {
  /**
   * NX-6140: narrow to submissions with something to act on - a payslip
   * Revenue rejected, or a message that never landed. The dashboard's "PSR
   * Error Submissions" tile sets it; the full screen and the history tile
   * never do, so there is no user-facing control for it.
   *
   * Read straight from the prop rather than seeded into filter state: the two
   * dashboard tiles render the SAME component, so React keeps the mounted
   * instance when you close one and open the other, and a state copy of this
   * would survive into the history modal and show only the errors.
   */
  hasErrors?: boolean;
  /**
   * Rendered inside a dashboard modal. The modal supplies its own title and
   * close button, so the page chrome - heading, Export All Errors, the card
   * wrapper - is dropped rather than nested inside it.
   */
  isDashboard?: boolean;
};

/**
 * NX-6140: Revenue filings, one row per submission.
 *
 * The per-worker listing stays alongside this one. It is still the right view
 * for anything filed before batching - back then every worker genuinely was
 * their own submission, so a batch view of that history would be 21,000 rows
 * of one.
 */
const PSRBatchListing: React.FC<Props> = ({
  hasErrors = false,
  isDashboard = false,
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isBureauEmployer, user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { psrBatchData } = useSelector((state: AppState) => state.revenueStore);
  const { list, pagination, loading } = psrBatchData;

  const [filter, setFilter] = useState<PSRBatchFilterType>({
    search: '',
    taxYear: getTaxWeekTaxYear().taxYear.toString(),
    payFrequency: null,
    taxPeriod: null,
    companyRefs: [],
  });

  const debouncedSearchTerm = useDebounce(filter.search || '', 500);

  const [downloadRecord, setDownloadRecord] =
    useState<PsrBatchRecordType | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  /**
   * The list endpoint returns counts, not the message, so the file has to be
   * built from the submission itself. Fetched straight rather than through the
   * slice: the slice's psrBatchDetail belongs to the submission page, and
   * filling it from here would leave that page showing a row nobody opened.
   */
  const onSelectDownload = async (type: DownloadType) => {
    const record = downloadRecord;
    setDownloadRecord(null);
    if (!record) return;

    setDownloadingId(record.id);
    try {
      const response = await jwtAxios.get(
        endPoints.revenueFilings.psrBatchDetail(record.id),
      );
      const ok = downloadPsrBatch(
        response.data?.data,
        type,
        showCurrencyByCountry(payrollCountry),
      );
      if (!ok) {
        showToast({
          message: messages['common.noDataToDownload'],
          type: 'error',
        });
      }
    } catch (error: any) {
      showToast({
        message: error?.response?.data?.message || error.message,
        type: 'error',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleGetBatches = (
    params: PSRBatchFilterType & PaginationParamsType,
  ) => {
    dispatch(
      revenueActions.getPsrBatches({
        search: params.search || undefined,
        taxYears: params.taxYear ? [params.taxYear] : [],
        payFrequencies: params.payFrequency ? [params.payFrequency] : [],
        taxPeriods: params.taxPeriod ? [params.taxPeriod.toString()] : [],
        companyIds: isBureauEmployer
          ? user?.companyUserIds || []
          : params.companyRefs.map((ref) => ref.value || ''),
        hasErrors: hasErrors ? 'true' : undefined,
        page: params.page || 0,
        size: params.size || 20,
      }),
    );
  };

  useEffect(() => {
    handleGetBatches({
      ...filter,
      search: debouncedSearchTerm,
      page: 0,
      size: 20,
    });
  }, [
    debouncedSearchTerm,
    filter.taxYear,
    filter.payFrequency,
    filter.taxPeriod,
    filter.companyRefs,
    // The two dashboard tiles render this same component, so the prop can
    // change without a remount; without it here the history modal would keep
    // showing whatever the error modal last fetched.
    hasErrors,
  ]);

  /**
   * Every rejected payslip across the submissions on screen, as one file.
   *
   * Same shape as the FPS, EPS and CIS300 screens: the listing's own filters
   * go up, an empty result says so rather than writing an empty file, and the
   * export is unpaginated - these are errors to work through, and a partial
   * list of them is worse than none.
   */
  const handleExportAllErrors = async () => {
    const rows = await dispatch(
      revenueActions.exportPsrBatchErrors({
        taxYears: filter.taxYear ? [filter.taxYear] : [],
        payFrequencies: filter.payFrequency ? [filter.payFrequency] : [],
        taxPeriods: filter.taxPeriod ? [filter.taxPeriod.toString()] : [],
        companyIds: resolveFilterCompanyIds(
          filter.companyRefs.map((ref) => ref.value || ''),
          isBureauEmployer,
          user?.companyUserIds,
        ),
      }),
    ).unwrap();

    if (!rows.length) {
      showToast({ type: 'error', message: messages['common.noError'] });
      return;
    }
    onExportPSRError(rows, 'PSR-Submission-Errors');
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetBatches({
      ...filter,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const isFiltering = Boolean(
    filter.search ||
      filter.payFrequency ||
      filter.taxPeriod ||
      filter.companyRefs.length ||
      hasErrors,
  );

  const columns = getPSRBatchColumns({
    messages,
    handleClickView: (record: PsrBatchRecordType) =>
      navigate(`/revenue-filings/psr-submissions/${record.id}`),
    handleClickDownload: (record: PsrBatchRecordType) =>
      setDownloadRecord(record),
    downloadingId,
  });

  const renderContent = () => (
    <>
      <AppsHeader>
        <PSRHeader
          taxYear={filter.taxYear ? parseInt(filter.taxYear, 10) : 0}
          filters={{
            payFrequency: filter.payFrequency,
            taxPeriod: filter.taxPeriod,
            // Status is a per-PAYSLIP verdict; a submission's state is shown
            // in its own column and does not filter the same way.
            status: null,
            companyRefs: filter.companyRefs,
          }}
          searchTerm={filter.search}
          setTaxYear={(taxYear) =>
            setFilter({ ...filter, taxYear: taxYear.toString() })
          }
          onApplyFilters={(values) =>
            setFilter({
              ...filter,
              payFrequency: values.payFrequency,
              // Clearing the frequency clears the period with it: tax period
              // numbering only means something within a frequency.
              taxPeriod: values.payFrequency ? values.taxPeriod : null,
              companyRefs: values.companyRefs,
            })
          }
          onChangeSearchTerm={(search) => setFilter({ ...filter, search })}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          columns={columns}
          titleEmpty={messages['revenue.psr.empty']}
          onChangePagination={onChangePagination}
          noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
        />
      </AppsContent>

      <SelectDataTypeModal
        isOpen={Boolean(downloadRecord)}
        toggle={() => setDownloadRecord(null)}
        onSelect={onSelectDownload}
        showPayroll
        loading={Boolean(downloadingId)}
      />
    </>
  );

  // The dashboard modal brings its own title and close button, so the page
  // heading and Export All Errors would sit inside a second header bar.
  if (isDashboard) return renderContent();

  return (
    <AppsContainer
      title={messages['revenue.psr.title']}
      type="bottom"
      fullView
      // Top-right of the page, as on FPS, EPS and CIS300 - the three screens
      // that already carry this button.
      rightContent={
        <div className="flex gap-2">
          <AppButton
            name={messages['common.exportErrorAll']}
            type="primary"
            variant="outlined"
            onClick={handleExportAllErrors}
          />
        </div>
      }
    >
      <AppCard>{renderContent()}</AppCard>
    </AppsContainer>
  );
};

export default PSRBatchListing;
